import { ReactNode, useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { SearchIcon } from "~/assets/icons";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";
import noop from "~/utils/noop";

interface EmbeddedContextualSearchProps {
  loading: boolean;
  currentPathLabel?: ReactNode;
  submitCallback?: (params: { query: string; root: boolean }) => any;
}

/**
 * Shadcn rebuild of `~/components/ContextualSearch`, for the nextgen-embedded
 * Files tab only. Same query-param-driven search and folder-scope choice
 * (search here vs. everywhere) as the original, built as a plain input +
 * absolutely-positioned dropdown instead of MUI's `Autocomplete`.
 */
const EmbeddedContextualSearch = ({
  loading,
  currentPathLabel,
  submitCallback = noop,
}: EmbeddedContextualSearchProps) => {
  const [params, setParams] = useQueryParams({
    q: withDefault(StringParam, ""),
    page: NumberParam,
    root: BooleanParam,
  });
  const [search, setSearch] = useState(params.q);
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);

  useEffect(() => {
    // Reset input on url change
    setSearch(params.q ?? "");
  }, [params.q]);

  const clearSearch = () => {
    setParams({ q: undefined, page: undefined });
  };

  const commitSearch = (root: boolean) => {
    if (!search) {
      return;
    }
    // Set search in url and reset pagination
    setParams({ q: search, page: undefined });
    submitCallback({ query: search, root });
  };

  const showScopeMenu = scopeMenuOpen && !!search && !!currentPathLabel;

  return (
    <div className="relative w-full">
      <SearchIcon
        width={14}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        value={search}
        onChange={(e) => {
          const value = e.target.value;
          setSearch(value);
          if (!value) {
            clearSearch();
          }
        }}
        onFocus={() => setScopeMenuOpen(true)}
        onBlur={() => setScopeMenuOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitSearch(false);
          }
        }}
        placeholder="Search"
        autoComplete="off"
        className="h-9 pl-8 pr-14"
      />
      <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
        {loading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        {search && (
          <button
            type="button"
            aria-label="Clear search"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setSearch("");
              if (params.q) {
                // If clearing previous query, remove query string params and
                // restore the previous directory view
                clearSearch();
              }
            }}
            className="rounded-sm p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
      {showScopeMenu && (
        <div
          role="listbox"
          className={cn(
            "absolute top-[calc(100%+4px)] left-0 z-50 w-full rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
          )}
        >
          <button
            type="button"
            role="option"
            aria-selected={false}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => commitSearch(false)}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
          >
            <SearchIcon width={12} className="shrink-0" />
            <span className="line-clamp-2">
              Search in <span className="font-semibold">{currentPathLabel}</span>
            </span>
          </button>
          <button
            type="button"
            role="option"
            aria-selected={false}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => commitSearch(true)}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
          >
            <SearchIcon width={12} className="shrink-0" />
            <span>Search everywhere</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default EmbeddedContextualSearch;
